/**
 * Source health, and the one write this interface performs.
 *
 * An operator needs to see that a parse failed rather than discovering it as a
 * silent price drop, so a failed run is shown as a failure with its diagnostics
 * while the previously accepted prices stay readable everywhere else.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { CrawlRun } from "@pricing/contracts";
import { client } from "../lib/api.ts";
import { contextParams } from "../lib/url-state.ts";
import { ContextBar } from "../components/ContextBar.tsx";
import { ageNow, formatInstant, timeUntil } from "../lib/format.ts";
import type { ViewProps } from "./PricesView.tsx";

export function SourcesView({ state }: ViewProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const [watching, setWatching] = useState<string | null>(null);

  const sources = useQuery({
    queryKey: ["sources", contextParams(state)],
    queryFn: () => client.sources(contextParams(state) as never),
    // Cooldown eligibility changes with time even when no collection runs.
    // While a source is cooling down, ask again when the earliest cooldown
    // should end, and at least every 30 s; with nothing to wait for, don't.
    refetchInterval: (query) => {
      const remaining = (query.state.data?.data ?? [])
        .map((row) => row.next_manual_refresh_at_utc)
        .filter((at): at is string => at !== null)
        .map((at) => Date.parse(at) - Date.now());
      if (remaining.length === 0) return false;
      return Math.min(30_000, Math.max(5_000, Math.min(...remaining) + 1_000));
    },
  });

  const run = useQuery({
    enabled: watching !== null,
    queryKey: ["crawl-run", watching],
    queryFn: () => client.crawlRun(watching!),
    // Poll until the run reaches a terminal state, then stop.
    refetchInterval: (query) => {
      const status = (query.state.data?.data as CrawlRun | undefined)?.status;
      return status && ["succeeded", "partial", "failed", "cancelled"].includes(status) ? false : 1000;
    },
  });

  const refresh = useMutation({
    // No credential: anyone who can reach the service may start a run. The
    // server admits one run per source at a time and only visits configured
    // sources.
    mutationFn: async (sourceId: string) =>
      (await client.startCrawlRun(sourceId, `explorer-${sourceId}-${Date.now()}`)).data as CrawlRun,
    onSuccess: (created) => setWatching(created.run_id),
    // A refused start (cooling down, busy) also means these rows are out of
    // date, so they are read again either way.
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ["sources"] }),
  });

  const rows = sources.data?.data ?? [];
  const watched = run.data?.data as CrawlRun | undefined;

  return (
    <>
      <section className="panel">
        <h2 className="panel__title">Refresh a source</h2>
        <p className="stat__denominator" style={{ maxWidth: "44rem" }}>
          Refresh starts a collection run for one source now. It visits only that source&apos;s
          configured pages, and a source runs one collection at a time. When scheduled collection
          is on, it runs inside the service at each source&apos;s own cadence.
        </p>
        {refresh.isError ? (
          <p className="notice notice--danger" role="alert">
            {(refresh.error as Error).message}
          </p>
        ) : null}
        {watched ? (
          <div
            className={`notice ${watched.status === "failed" ? "notice--danger" : watched.status === "partial" ? "notice--warn" : ""}`}
            aria-live="polite"
          >
            <strong>
              Run {watched.run_id} — {watched.status} ({watched.phase})
            </strong>
            <ul style={{ margin: "var(--space-2) 0 0", paddingLeft: "var(--space-6)" }}>
              {watched.unit_results.map((unit) => (
                <li key={unit.unit_id}>
                  {unit.unit_id}: {unit.outcome} — accepted {unit.accepted_count}, unchanged{" "}
                  {unit.unchanged_count}, rejected {unit.rejected_count}
                  {unit.missing_offering_ids.length > 0
                    ? `, ${unit.missing_offering_ids.length} offering(s) not observed (previous prices retained)`
                    : ""}
                </li>
              ))}
            </ul>
            {watched.diagnostics.filter((entry) => entry.severity !== "info").length > 0 ? (
              <ul style={{ margin: "var(--space-2) 0 0", paddingLeft: "var(--space-6)" }}>
                {watched.diagnostics
                  .filter((entry) => entry.severity !== "info")
                  .map((entry, index) => (
                    <li key={`${entry.code}-${index}`}>
                      <strong>{entry.severity}:</strong> {entry.code} — {entry.message}
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="panel">
        <h2 className="panel__title">Configured sources</h2>
        {sources.isPending ? <p className="notice">Loading sources…</p> : null}
        <div className="table-wrap">
          <table>
            <caption className="visually-hidden">
              Each configured source, when it was last reached and when it last produced an accepted
              price
            </caption>
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col">Last run</th>
                <th scope="col">Last fetch</th>
                <th scope="col">Last accepted</th>
                <th scope="col" className="numeric">
                  Offerings
                </th>
                <th scope="col">Coverage</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const coolingDown = row.next_manual_refresh_at_utc !== null;
                return (
                  <tr key={row.source_id}>
                    <th scope="row">
                      {row.source_id}
                      <div className="stat__denominator">
                        {row.kind} · adapter {row.adapter_version} · config {row.config_version}
                      </div>
                    </th>
                    <td>
                      {row.last_status === null ? (
                        <span className="badge">never run</span>
                      ) : (
                        <span
                          className={`badge ${row.last_status === "succeeded" ? "badge--ok" : row.last_status === "failed" ? "badge--failed" : "badge--stale"}`}
                        >
                          {row.last_status}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.last_fetch_at_utc === null ? (
                        "—"
                      ) : (
                        <span title={formatInstant(row.last_fetch_at_utc)}>
                          {ageNow(row.last_fetch_at_utc)}
                        </span>
                      )}
                    </td>
                    <td>
                      {row.last_accepted_at_utc === null ? (
                        // Stale implies something went out of date; nothing was
                        // ever collected from this source.
                        <span className="badge">never collected</span>
                      ) : (
                        <>
                          <span title={formatInstant(row.last_accepted_at_utc)}>
                            {ageNow(row.last_accepted_at_utc)}
                          </span>{" "}
                          {row.stale ? <span className="badge badge--stale">stale</span> : null}
                        </>
                      )}
                    </td>
                    <td className="numeric">{row.accepted_offering_count}</td>
                    <td className="cell--wrap">
                      {row.coverage_units.map((unit) => unit.unit_id).join(", ")}
                      {row.stale_unit_ids.length > 0 ? (
                        <div className="stat__denominator">
                          not refreshed: {row.stale_unit_ids.join(", ")}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {row.kind === "import" ? (
                        // Nothing to collect: an operator supplies each artifact.
                        <span className="stat__denominator">Imported with the CLI</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="button"
                            disabled={!row.enabled || refresh.isPending || coolingDown}
                            onClick={() => refresh.mutate(row.source_id)}
                          >
                            Refresh
                          </button>
                          {coolingDown ? (
                            <div
                              className="stat__denominator"
                              title="Manual refreshes are limited per source. Scheduled collection is unaffected."
                            >
                              Next refresh in {timeUntil(row.next_manual_refresh_at_utc!)}.
                            </div>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {rows.some((row) => row.diagnostics.length > 0) ? (
          <div style={{ marginTop: "var(--space-4)", display: "grid", gap: "var(--space-2)" }}>
            <h3 style={{ fontSize: "var(--text-base)" }}>Recent diagnostics</h3>
            {rows.flatMap((row) =>
              row.diagnostics.slice(0, 5).map((diagnostic, index) => (
                <p key={`${row.source_id}-${index}`} className="notice notice--warn">
                  <strong>{row.source_id}</strong> — {diagnostic.code}: {diagnostic.message}
                </p>
              )),
            )}
          </div>
        ) : null}
      </section>

      {sources.data ? (
        <ContextBar
          catalogRevisionId={sources.data.catalog_revision_id}
          observationSequence={sources.data.observation_sequence}
          observationCutoffUtc={sources.data.observation_cutoff_utc}
        />
      ) : null}
    </>
  );
}
